import React, { useState, useEffect, useRef } from 'react';
import { photoAPI, imagaeAPI } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal, Button } from 'react-bootstrap';

const Photos = () => {
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    const [galleryImages, setGalleryImages] = useState([]);

    const [formData, setFormData] = useState({
        title: '',
        subtitle: '',
        order: 0,
        status: true,
        main_image: null,
        gallery_images: [],
        gallery_captions: []
    });

    const mainImageRef = useRef(null);
    const editMainImageRef = useRef(null);
    const galleryInputRef = useRef(null);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await photoAPI.getAll();
            setItems(res.data.data);
        } catch (error) {
            console.error("Error fetching photos:", error);
            toast.error("Failed to load photo galleries");
        } finally {
            setLoading(false);
        }
    };

    const dataload = items.filter(item => item.order === 0);

    useEffect(() => {
        fetchItems();
    }, []);

    const handleChange = (e) => {
        const { name, value, files, type, checked } = e.target;
        if (type === 'file' && name === 'main_image') {
            setFormData(prev => ({
                ...prev,
                [name]: files[0]
            }));
        } else if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleGalleryImagesChange = (e) => {
        const files = Array.from(e.target.files);
        setFormData(prev => ({
            ...prev,
            gallery_images: files,
            gallery_captions: files.map(() => '')
        }));
    };

    const handleGalleryCaptionChange = (index, value) => {
        const newCaptions = [...formData.gallery_captions];
        newCaptions[index] = value;
        setFormData(prev => ({
            ...prev,
            gallery_captions: newCaptions
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = new FormData();
            data.append("title", formData.title);
            data.append("subtitle", formData.subtitle);
            data.append("order", formData.order);
            data.append("status", formData.status ? 1 : 0);

            if (formData.main_image) {
                data.append("main_image", formData.main_image);
            }

            formData.gallery_images.forEach((file, index) => {
                data.append(`gallery_images[${index}]`, file);
                if (formData.gallery_captions[index]) {
                    data.append(`gallery_captions[${index}]`, formData.gallery_captions[index]);
                }
            });

            await photoAPI.create(data);
            toast.success('Photo gallery created successfully!');
            setFormData({
                title: '',
                subtitle: '',
                order: 0,
                status: true,
                main_image: null,
                gallery_images: [],
                gallery_captions: []
            });
            if (mainImageRef.current) mainImageRef.current.value = "";
            if (galleryInputRef.current) galleryInputRef.current.value = "";
            fetchItems();
        } catch (error) {
            console.error(error);
            toast.error('Error creating gallery: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (item) => {
        setSelectedItem({
            id: item.id,
            title: item.title,
            subtitle: item.subtitle || '',
            order: item.order,
            status: item.status,
            main_image: item.main_image,
            galleryImages: item.gallery_images || [],
            _original_main_image: item.main_image,
            deleted_gallery_ids: [],
            gallery_captions: (item.gallery_images || []).map(img => img.caption || '')
        });
        setShowEditModal(true);
    };

    const handleEditChange = (e) => {
        const { name, value, files, type, checked } = e.target;
        if (type === 'file' && name === 'main_image') {
            setSelectedItem(prev => ({
                ...prev,
                [name]: files[0]
            }));
        } else if (type === 'checkbox') {
            setSelectedItem(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setSelectedItem(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleEditGalleryCaptionChange = (index, value) => {
        const newCaptions = [...selectedItem.gallery_captions];
        newCaptions[index] = value;
        setSelectedItem(prev => ({
            ...prev,
            gallery_captions: newCaptions
        }));
    };

    const handleEditGalleryImagesChange = (e) => {
        const files = Array.from(e.target.files);
        setSelectedItem(prev => ({
            ...prev,
            new_gallery_images: files,
            new_gallery_captions: files.map(() => '')
        }));
    };

    const handleRemoveExistingGalleryImage = (index) => {
        const galleryId = selectedItem.galleryImages[index].id;
        setSelectedItem(prev => ({
            ...prev,
            deleted_gallery_ids: [...prev.deleted_gallery_ids, galleryId],
            galleryImages: prev.galleryImages.filter((_, i) => i !== index),
            gallery_captions: prev.gallery_captions.filter((_, i) => i !== index)
        }));
    };

    const handleUpdateItem = async () => {
        setLoading(true);
        try {
            const data = new FormData();
            data.append("title", selectedItem.title);
            data.append("subtitle", selectedItem.subtitle);
            data.append("order", selectedItem.order);
            data.append("status", selectedItem.status ? 1 : 0);

            if (selectedItem.main_image && selectedItem.main_image instanceof File) {
                data.append("main_image", selectedItem.main_image);
            }

            selectedItem.galleryImages.forEach(img => {
                data.append("existing_gallery_ids[]", img.id);
            });

            selectedItem.gallery_captions.forEach((caption, index) => {
                data.append(`gallery_captions[${index}]`, caption);
            });

            selectedItem.deleted_gallery_ids.forEach(id => {
                data.append("deleted_gallery_ids[]", id);
            });

            if (selectedItem.new_gallery_images) {
                selectedItem.new_gallery_images.forEach((file, index) => {
                    data.append(`gallery_images[${index}]`, file);
                    if (selectedItem.new_gallery_captions && selectedItem.new_gallery_captions[index]) {
                        data.append(`gallery_captions[${selectedItem.galleryImages.length + index}]`, selectedItem.new_gallery_captions[index]);
                    }
                });
            }

            await photoAPI.update(selectedItem.id, data);
            toast.success('Photo gallery updated successfully!');
            setShowEditModal(false);
            fetchItems();
        } catch (error) {
            console.error(error);
            toast.error('Error updating gallery: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        setLoading(true);
        try {
            await photoAPI.toggleStatus(id);
            toast.success(`Gallery ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
            fetchItems();
        } catch (error) {
            console.error(error);
            toast.error('Error updating status: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleViewGallery = (images) => {
        setGalleryImages(images);
        setShowGalleryModal(true);
    };

    const confirmDelete = (id) => {
        toast.info(
            <div>
                <p style={{ marginBottom: '0.5rem' }}>Are you sure you want to delete this gallery? All images will be permanently deleted.</p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button
                        className="btn btn-sm btn-danger"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        onClick={() => { toast.dismiss(); handleDeleteItem(id); }}
                    >
                        Yes
                    </button>
                    <button
                        className="btn btn-sm btn-secondary"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        onClick={() => toast.dismiss()}
                    >
                        No
                    </button>
                </div>
            </div>,
            { autoClose: false, closeOnClick: false, closeButton: false, icon: false }
        );
    };

    const handleDeleteItem = async (id) => {
        setLoading(true);
        try {
            await photoAPI.delete(id);
            toast.success("Gallery deleted successfully!");
            fetchItems();
        } catch (error) {
            console.error(error);
            toast.error("Error deleting gallery: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    // ----- inline styles -----
    const mainContentStyle = {
        marginLeft: '260px',
        padding: '20px 30px',
        backgroundColor: '#f4f7fc',
        minHeight: '100vh',
    };

    const contentWrapperStyle = {
        maxWidth: '1200px',
        margin: '0 auto',
    };

    const formCardStyle = {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        overflow: 'hidden',
        marginBottom: '25px',
    };

    const formCardHeaderStyle = {
        background: 'linear-gradient(135deg, #0b2b3b 0%, #0a1c28 100%)',
        padding: '16px 24px',
        color: 'white',
    };

    const formCardHeaderTitleStyle = {
        margin: 0,
        fontSize: '1rem',
        fontWeight: 600,
    };

    const formCardBodyStyle = {
        padding: '24px',
    };

    const formRowStyle = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '1rem',
    };

    const formGroupStyle = (flex = 1) => ({
        flex: flex,
        minWidth: '200px',
    });

    const labelStyle = {
        display: 'block',
        marginBottom: '0.25rem',
        fontWeight: 600,
        fontSize: '0.9rem',
        color: '#1e293b',
    };

    const inputStyle = {
        width: '100%',
        padding: '0.5rem 0.75rem',
        fontSize: '0.85rem',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        transition: 'all 0.2s',
        outline: 'none',
        background: 'white',
    };

    const inputFocusStyle = {
        borderColor: '#2c7da0',
        boxShadow: '0 0 0 3px rgba(44,125,160,0.1)',
    };

    const smallTextStyle = {
        fontSize: '0.7rem',
        color: '#64748b',
        display: 'block',
        marginTop: '0.25rem',
    };

    const buttonPrimaryStyle = {
        background: 'linear-gradient(135deg, #0b2b3b 0%, #0a1c28 100%)',
        border: 'none',
        padding: '10px 25px',
        fontSize: '0.85rem',
        fontWeight: 600,
        borderRadius: '10px',
        color: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s',
    };

    const buttonPrimaryHoverStyle = {
        transform: 'translateY(-2px)',
        boxShadow: '0 5px 15px rgba(11,43,59,0.3)',
    };

    const listCardStyle = {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        overflow: 'hidden',
        marginTop: '1.5rem',
    };

    const tableContainerStyle = {
        overflowX: 'auto',
        padding: '0 1rem',
    };

    const tableStyle = {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '0.85rem',
    };

    const tableHeaderStyle = {
        padding: '14px 20px',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: '1px solid #e2e8f0',
        background: '#f8fafc',
        textAlign: 'left',
    };

    const tableCellStyle = {
        padding: '14px 20px',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'middle',
    };

    const badgeStyle = {
        background: '#e9f7ef',
        color: '#1e7b48',
        fontWeight: 600,
        padding: '4px 12px',
        borderRadius: '40px',
        fontSize: '0.7rem',
        display: 'inline-block',
    };

    const actionButtonStyle = {
        padding: '0.25rem 0.5rem',
        fontSize: '0.75rem',
        borderRadius: '4px',
        border: 'none',
        cursor: 'pointer',
        marginRight: '0.25rem',
    };

    // State for button hover
    const [hoveredButton, setHoveredButton] = useState(null);

    return (
        <>
            <div style={mainContentStyle}>
                <div style={contentWrapperStyle}>
                    {/* Add Form */}
                    <div style={formCardStyle}>
                        <div style={formCardHeaderStyle}>
                            <h5 style={formCardHeaderTitleStyle}>
                                <i className="fas fa-plus-circle" style={{ marginRight: '0.5rem' }}></i>Create Photo Gallery
                            </h5>
                        </div>
                        <div style={formCardBodyStyle}>
                            <form onSubmit={handleSubmit}>
                                <div style={formRowStyle}>
                                    <div style={formGroupStyle(2)}>
                                        <label style={labelStyle}>Title *</label>
                                        <input
                                            type="text"
                                            style={inputStyle}
                                            name="title"
                                            value={formData.title}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div style={{ ...formGroupStyle(1), display: 'none' }}>
                                        <label style={labelStyle}>Subtitle</label>
                                        <input
                                            type="text"
                                            style={inputStyle}
                                            name="subtitle"
                                            value={formData.subtitle}
                                            onChange={handleChange}
                                            placeholder="Optional subtitle"
                                        />
                                    </div>
                                    <div style={{ ...formGroupStyle(0.5), display: 'none' }}>
                                        <label style={labelStyle}>Display Order</label>
                                        <input
                                            type="number"
                                            style={inputStyle}
                                            name="order"
                                            value={formData.order}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div style={formRowStyle}>
                                    <div style={formGroupStyle(1)}>
                                        <label style={labelStyle}>Main Image</label>
                                        <input
                                            type="file"
                                            style={inputStyle}
                                            name="main_image"
                                            onChange={handleChange}
                                            accept="image/*"
                                            ref={mainImageRef}
                                        />
                                        <small style={smallTextStyle}>Main thumbnail for the gallery</small>
                                    </div>
                                    <div style={{ ...formGroupStyle(1), display: 'none' }}>
                                        <label style={labelStyle}>Status</label>
                                        <div style={{ marginTop: '0.5rem' }}>
                                            <input
                                                type="checkbox"
                                                name="status"
                                                checked={formData.status}
                                                onChange={handleChange}
                                            /> Active
                                        </div>
                                    </div>
                                </div>
                                <div style={formGroupStyle(1)}>
                                    <label style={labelStyle}>Gallery Images</label>
                                    <input
                                        type="file"
                                        style={inputStyle}
                                        name="gallery_images"
                                        onChange={handleGalleryImagesChange}
                                        accept="image/*"
                                        multiple
                                        ref={galleryInputRef}
                                    />
                                    <small style={smallTextStyle}>You can select multiple images at once</small>
                                </div>

                                {/* Gallery Images Preview with Captions */}
                                {formData.gallery_images.length > 0 && (
                                    <div style={{ marginTop: '1rem' }}>
                                        <label style={labelStyle}>Image Captions</label>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                                            {formData.gallery_images.map((file, index) => (
                                                <div key={index} style={{ flex: '0 0 calc(25% - 1rem)', minWidth: '150px' }}>
                                                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                                                        <img
                                                            src={URL.createObjectURL(file)}
                                                            alt={`Preview ${index}`}
                                                            style={{ height: '150px', width: '100%', objectFit: 'cover' }}
                                                        />
                                                        <div style={{ padding: '0.5rem' }}>
                                                            <input
                                                                type="text"
                                                                style={{ ...inputStyle, fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                                                placeholder="Caption"
                                                                value={formData.gallery_captions[index]}
                                                                onChange={(e) => handleGalleryCaptionChange(index, e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    style={buttonPrimaryStyle}
                                    disabled={loading}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'none'}
                                >
                                    {loading ? "Creating..." : "Create Gallery"}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* List Table */}
                    <div style={listCardStyle}>
                        <div style={tableContainerStyle}>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={tableHeaderStyle}>ID</th>
                                        <th style={tableHeaderStyle}>Main Image</th>
                                        <th style={tableHeaderStyle}>Title</th>
                                        <th style={{ ...tableHeaderStyle, display: 'none' }}>Subtitle</th>
                                        <th style={tableHeaderStyle}>Images Count</th>
                                        <th style={{ ...tableHeaderStyle, display: 'none' }}>Order</th>
                                        <th style={{ ...tableHeaderStyle, display: 'none' }}>Status</th>
                                        <th style={tableHeaderStyle}>View</th>
                                        <th style={tableHeaderStyle}>Edit</th>
                                        <th style={tableHeaderStyle}>Delete</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dataload.map((item) => (
                                        <tr key={item.id} style={{ transition: 'background 0.15s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                                            <td style={tableCellStyle}>{item.id}</td>
                                            <td style={tableCellStyle}>
                                                {item.main_image ? (
                                                    <img
                                                        src={`${imagaeAPI}/uploads/photos/${item.main_image}`}
                                                        alt={item.title}
                                                        width="50"
                                                        height="50"
                                                        style={{ objectFit: "cover", cursor: "pointer", borderRadius: '4px' }}
                                                        onClick={() => window.open(`${imagaeAPI}/uploads/photos/${item.main_image}`, "_blank")}
                                                    />
                                                ) : "-"}
                                            </td>
                                            <td style={tableCellStyle}>{item.title}</td>
                                            <td style={{ ...tableCellStyle, display: 'none' }}>{item.subtitle || '-'}</td>
                                            <td style={tableCellStyle}>
                                                <span style={badgeStyle}>{item.gallery_images?.length || 0} images</span>
                                            </td>
                                            <td style={{ ...tableCellStyle, display: 'none' }}>{item.order}</td>
                                            <td style={{ ...tableCellStyle, display: 'none' }}>
                                                <button
                                                    style={{
                                                        ...actionButtonStyle,
                                                        background: item.status ? '#28a745' : '#6c757d',
                                                        color: 'white'
                                                    }}
                                                    onClick={() => handleToggleStatus(item.id, item.status)}
                                                    disabled={loading}
                                                >
                                                    {item.status ? 'Active' : 'Inactive'}
                                                </button>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <button
                                                    style={{ ...actionButtonStyle, background: '#007bff', color: 'white' }}
                                                    onClick={() => handleViewGallery(item.gallery_images)}
                                                >
                                                    <i className="fas fa-images"></i> View
                                                </button>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <a href="#" onClick={(e) => { e.preventDefault(); handleEditClick(item); }} style={{ color: '#2c7da0', textDecoration: 'none' }}>
                                                    <i className="fas fa-edit"></i>
                                                </a>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <a href="#" onClick={(e) => { e.preventDefault(); confirmDelete(item.id); }} style={{ color: '#dc2626', textDecoration: 'none' }}>
                                                    <i className="fas fa-trash"></i>
                                                </a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <ToastContainer />
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Photo Gallery</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedItem && (
                        <form>
                            <div style={formRowStyle}>
                                <div style={formGroupStyle(2)}>
                                    <label style={labelStyle}>Title *</label>
                                    <input
                                        type="text"
                                        style={inputStyle}
                                        name="title"
                                        value={selectedItem.title}
                                        onChange={handleEditChange}
                                        required
                                    />
                                </div>
                                <div style={{ ...formGroupStyle(1), display: 'none' }}>
                                    <label style={labelStyle}>Subtitle</label>
                                    <input
                                        type="text"
                                        style={inputStyle}
                                        name="subtitle"
                                        value={selectedItem.subtitle}
                                        onChange={handleEditChange}
                                    />
                                </div>
                                <div style={{ ...formGroupStyle(0.5), display: 'none' }}>
                                    <label style={labelStyle}>Display Order</label>
                                    <input
                                        type="number"
                                        style={inputStyle}
                                        name="order"
                                        value={selectedItem.order}
                                        onChange={handleEditChange}
                                    />
                                </div>
                            </div>
                            <div style={formRowStyle}>
                                <div style={formGroupStyle(1)}>
                                    <label style={labelStyle}>Main Image</label>
                                    <input
                                        type="file"
                                        style={inputStyle}
                                        name="main_image"
                                        onChange={handleEditChange}
                                        accept="image/*"
                                        ref={editMainImageRef}
                                    />
                                    {selectedItem._original_main_image && (
                                        <div style={{ marginTop: '0.5rem' }}>
                                            <img
                                                src={`${imagaeAPI}/uploads/photos/${selectedItem._original_main_image}`}
                                                alt="Current"
                                                width="80"
                                            />
                                            <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>Current main image</p>
                                        </div>
                                    )}
                                </div>
                                <div style={{ ...formGroupStyle(1), display: 'none' }}>
                                    <label style={labelStyle}>Status</label>
                                    <div style={{ marginTop: '0.5rem' }}>
                                        <input
                                            type="checkbox"
                                            name="status"
                                            checked={selectedItem.status}
                                            onChange={handleEditChange}
                                        /> Active
                                    </div>
                                </div>
                            </div>

                            {/* Existing Gallery Images */}
                            {selectedItem.galleryImages && selectedItem.galleryImages.length > 0 && (
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={labelStyle}>Existing Gallery Images</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                                        {selectedItem.galleryImages.map((img, index) => (
                                            <div key={img.id} style={{ flex: '0 0 calc(25% - 1rem)', minWidth: '150px' }}>
                                                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                                                    <img
                                                        src={`${imagaeAPI}/uploads/photos/${img.image}`}
                                                        alt={img.caption || 'Gallery'}
                                                        style={{ height: '150px', width: '100%', objectFit: 'cover' }}
                                                    />
                                                    <div style={{ padding: '0.5rem' }}>
                                                        <input
                                                            type="text"
                                                            style={{ ...inputStyle, fontSize: '0.8rem', padding: '0.25rem 0.5rem', marginBottom: '0.5rem' }}
                                                            placeholder="Caption"
                                                            value={selectedItem.gallery_captions[index]}
                                                            onChange={(e) => handleEditGalleryCaptionChange(index, e.target.value)}
                                                        />
                                                        <button
                                                            type="button"
                                                            style={{ ...actionButtonStyle, background: '#dc3545', color: 'white', width: '100%' }}
                                                            onClick={() => handleRemoveExistingGalleryImage(index)}
                                                        >
                                                            <i className="fas fa-trash"></i> Remove
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Add New Gallery Images */}
                            <div style={formGroupStyle(1)}>
                                <label style={labelStyle}>Add New Gallery Images</label>
                                <input
                                    type="file"
                                    style={inputStyle}
                                    name="new_gallery_images"
                                    onChange={handleEditGalleryImagesChange}
                                    accept="image/*"
                                    multiple
                                />
                                <small style={smallTextStyle}>Select multiple images to add to gallery</small>
                            </div>
                        </form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>Close</Button>
                    <Button variant="primary" onClick={handleUpdateItem} disabled={loading}>
                        {loading ? "Updating..." : "Update"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* View Gallery Modal */}
            <Modal show={showGalleryModal} onHide={() => setShowGalleryModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Gallery Images</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                        {galleryImages.map((img, index) => (
                            <div key={img.id || index} style={{ flex: '0 0 calc(33.33% - 1rem)', minWidth: '200px' }}>
                                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                                    <img
                                        src={`${imagaeAPI}/uploads/photos/${img.image}`}
                                        alt={img.caption || 'Gallery'}
                                        style={{ height: '200px', width: '100%', objectFit: 'cover', cursor: 'pointer' }}
                                        onClick={() => window.open(`${imagaeAPI}/uploads/photos/${img.image}`, "_blank")}
                                    />
                                    {img.caption && (
                                        <div style={{ padding: '0.5rem' }}>
                                            <p style={{ fontSize: '0.8rem', margin: 0 }}>{img.caption}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowGalleryModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Photos;